import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService as Auth0, User } from '@auth0/auth0-angular';
import { AuthorizationParams } from '@auth0/auth0-spa-js';
import { Notifier } from '@core/services/notifier';
import { extractOrgFromHost } from '@core/utils/urls';
import { environment } from '@environments/environment';
import {
  catchError,
  filter,
  firstValueFrom,
  from,
  mergeMap,
  of,
  take,
  tap,
  throwError,
} from 'rxjs';

export type MfaProvider = 'guardian' | 'otp' | 'webauthn-roaming' | 'webauthn-platform' | 'duo';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly auth0 = inject(Auth0);
  private readonly notifier = inject(Notifier);

  readonly isAuthenticated = toSignal(this.auth0.isAuthenticated$, { initialValue: null });
  readonly auth0User = toSignal(this.auth0.user$, { initialValue: null });
  readonly error = toSignal(this.auth0.error$, { initialValue: null });

  private async getBaseAuthorizationParams() {
    const authorizationParams: AuthorizationParams = {};
    const organizationName = extractOrgFromHost(window.location.hostname);
    if (organizationName) {
      authorizationParams['organization'] = organizationName;
    }
    return authorizationParams;
  }

  async login({ returnTo = '/', promptLogin = false } = {}): Promise<void> {
    await firstValueFrom(
      this.auth0
        .loginWithRedirect({
          appState: { target: returnTo },
          authorizationParams: {
            ...(await this.getBaseAuthorizationParams()),
            prompt: promptLogin ? 'login' : undefined,
          },
        })
        .pipe(take(1)),
    );
  }

  async signup({ returnTo = '/' } = {}): Promise<void> {
    await firstValueFrom(
      this.auth0
        .loginWithRedirect({
          appState: { target: returnTo },
          authorizationParams: {
            ...(await this.getBaseAuthorizationParams()),
            screen_hint: 'signup',
          },
        })
        .pipe(take(1)),
    );
  }

  async logout({ returnTo = window.location.origin } = {}): Promise<void> {
    await firstValueFrom(
      this.auth0
        .logout({
          logoutParams: {
            ...(await this.getBaseAuthorizationParams()),
            returnTo,
          },
        })
        .pipe(take(1)),
    );
  }

  async getToken(audience?: string): Promise<string> {
    return await firstValueFrom(
      this.auth0
        .getAccessTokenSilently({
          authorizationParams: { audience },
        })
        .pipe(take(1)),
    );
  }

  async sendPasswordResetEmail(): Promise<void> {
    await firstValueFrom(
      this.auth0.user$.pipe(
        filter((user): user is User => user !== null),
        mergeMap((user) => {
          const domain = environment.auth0.domain;
          return from(
            fetch(`https://${domain}/dbconnections/change_password`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                client_id: environment.auth0.clientId,
                email: user.email,
                connection: environment.auth0.connection,
              }),
            }),
          ).pipe(
            mergeMap((response) => {
              if (!response.ok) {
                return throwError(() => new Error('Failed to send password reset email'));
              }
              return of(response);
            }),
            tap(() => {
              this.notifier.success({ title: 'Password reset email sent', duration: 10000 });
            }),
            catchError((error) => {
              this.notifier.error({
                title: 'Failed to send password reset email!',
                duration: 10000,
              });
              return throwError(() => error);
            }),
          );
        }),
        take(1),
      ),
    );
  }
}
